import { useEffect, useState, useCallback } from "react";
import { Globe2, Loader2, Save, Plus, X, ShieldAlert, UserCheck, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/common/DataTable";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Lista de países/jurisdicciones de alto riesgo GAFI (43ª-X-c) + clientes con país de riesgo.
export default function PaisesGafi() {
  const { user } = useAuth();
  const puede = ["oficial_cumplimiento", "admin_entidad", "analista_pld"].includes(user?.role);
  const puedeEditar = user?.role === "oficial_cumplimiento";

  const [negra, setNegra] = useState([]);
  const [gris, setGris] = useState([]);
  const [esDefault, setEsDefault] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nuevo, setNuevo] = useState({ negra: "", gris: "" });

  const load = useCallback(async () => {
    if (!puede) return;
    try {
      const [p, c] = await Promise.all([
        api.get("/pld/gafi/paises").then((r) => r.data),
        api.get("/pld/gafi/clientes-riesgo").then((r) => r.data).catch(() => ({ clientes: [] })),
      ]);
      setNegra(p.negra || []); setGris(p.gris || []); setEsDefault(p.es_default);
      setClientes(c.clientes || []);
    } catch (e) { console.debug("GAFI países/clientes (sin acceso):", e?.response?.status); }
  }, [puede]);
  useEffect(() => { load(); }, [load]);

  const guardar = async () => {
    setBusy(true);
    try {
      await api.put("/pld/gafi/paises", { negra, gris });
      toast.success("Lista GAFI actualizada.");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const importar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("¿Reemplazar la lista GAFI con el contenido del archivo (columnas: categoria, pais)?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/pld/gafi/importar`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "No se pudo importar.");
      toast.success(`Lista GAFI importada: ${data.negra.length} negra, ${data.gris.length} gris.`);
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const add = (cat) => {
    const v = nuevo[cat].trim();
    if (!v) return;
    if (cat === "negra" && !negra.includes(v)) setNegra([...negra, v].sort());
    if (cat === "gris" && !gris.includes(v)) setGris([...gris, v].sort());
    setNuevo((p) => ({ ...p, [cat]: "" }));
  };
  const del = (cat, v) => {
    if (cat === "negra") setNegra(negra.filter((x) => x !== v));
    else setGris(gris.filter((x) => x !== v));
  };

  if (!puede) return null;

  const Chips = ({ cat, items, tone }) => (
    <div className="flex flex-wrap gap-1.5" data-testid={`gafi-${cat}`}>
      {items.map((p) => (
        <Badge key={p} className={tone}>
          {p}
          {puedeEditar && <button onClick={() => del(cat, p)} className="ml-1"><X className="h-3 w-3" /></button>}
        </Badge>
      ))}
      {items.length === 0 && <span className="text-xs text-slate-400">Sin entradas.</span>}
    </div>
  );

  return (
    <div className="mb-6 space-y-4 rounded-lg border border-amber-200 bg-amber-50/40 p-4" data-testid="paises-gafi">
      <div className="flex items-start gap-2 text-xs text-amber-800">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <p><strong>Países/jurisdicciones de alto riesgo GAFI (43ª-X-c).</strong> Negra = alto riesgo / llamado a la acción; Gris = monitoreo intensificado. Eleva el factor geográfico del EBR y exige debida diligencia reforzada (no bloqueo automático). # REVISIÓN HUMANA — actualizar contra la lista vigente del GAFI (~3 veces al año). {esDefault && "Actualmente se usa la lista precargada por defecto."}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-red-200 bg-white p-3">
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-red-700"><Globe2 className="h-3.5 w-3.5" /> Lista negra (llamado a la acción)</p>
          <Chips cat="negra" items={negra} tone="bg-red-100 text-red-700" />
          {puedeEditar && (
            <div className="mt-2 flex gap-2">
              <Input data-testid="gafi-negra-input" className="h-8" placeholder="País…" value={nuevo.negra} onChange={(e) => setNuevo((p) => ({ ...p, negra: e.target.value }))} />
              <Button data-testid="gafi-negra-add" size="sm" variant="outline" onClick={() => add("negra")}><Plus className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
        <div className="rounded-md border border-amber-200 bg-white p-3">
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700"><Globe2 className="h-3.5 w-3.5" /> Lista gris (monitoreo intensificado)</p>
          <Chips cat="gris" items={gris} tone="bg-amber-100 text-amber-700" />
          {puedeEditar && (
            <div className="mt-2 flex gap-2">
              <Input data-testid="gafi-gris-input" className="h-8" placeholder="País…" value={nuevo.gris} onChange={(e) => setNuevo((p) => ({ ...p, gris: e.target.value }))} />
              <Button data-testid="gafi-gris-add" size="sm" variant="outline" onClick={() => add("gris")}><Plus className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      {puedeEditar && (
        <div className="flex justify-end gap-2">
          <input id="gafi-imp-file" data-testid="gafi-import-file" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={importar} />
          <Button data-testid="gafi-importar-btn" size="sm" variant="outline" disabled={busy} onClick={() => document.getElementById("gafi-imp-file").click()}>
            <Upload className="mr-1.5 h-4 w-4" /> Importar archivo (CSV/Excel)
          </Button>
          <Button data-testid="gafi-guardar-btn" size="sm" disabled={busy} onClick={guardar} className="bg-primary hover:bg-sidebar-hover">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar lista GAFI
          </Button>
        </div>
      )}

      <div>
        <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600"><UserCheck className="h-3.5 w-3.5" /> Clientes con país de riesgo (DDR)</h4>
        <DataTable testid="table-gafi-clientes" rows={clientes}
          columns={[
            { key: "cliente", label: "Cliente" },
            { key: "pais_detectado", label: "País detectado" },
            { key: "categoria", label: "Lista", render: (r) => <Badge className={r.categoria === "negra" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>{r.categoria}</Badge> },
            { key: "grado_riesgo", label: "Grado EBR" },
            { key: "ddr", label: "Acción" },
          ]}
          emptyLabel="Ningún cliente con país de riesgo GAFI." />
      </div>
    </div>
  );
}
